/**
 * SuperAdminLiveProviderSettingsPage.jsx
 * Super-admin only. Surfaces provider health + required env vars
 * for activation. This is the ONLY place provider names are visible.
 */
import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import {
  adminProviderSettings, reconcileRecordings, formatApiError,
} from "../../lib/liveClassApi";
import { toast } from "sonner";
import {
  Settings, CheckCircle2, AlertCircle, ShieldCheck, RefreshCw,
} from "lucide-react";

export default function SuperAdminLiveProviderSettingsPage() {
  const [data, setData] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  useEffect(() => {
    (async () => {
      try { setData(await adminProviderSettings()); }
      catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const reconcile = async () => {
    setReconciling(true);
    try {
      const r = await reconcileRecordings();
      toast.success(`Reconciled ${r.checked} / updated ${r.updated}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setReconciling(false); }
  };

  return (
    <DashboardLayout title="Live Provider Settings">
      <div data-testid="lmslive-provider-settings" className="space-y-6 pb-12">
        <section className="relative overflow-hidden rounded-3xl border border-[#0F4C81] bg-gradient-to-br from-[#0A192F] via-[#0F4C81] to-[#0A192F] p-6 md:p-7 shadow-2xl text-white">
          <div className="absolute -top-32 -right-24 h-72 w-72 rounded-full bg-[#D4AF37]/15 blur-3xl" />
          <div className="relative">
            <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] font-bold flex items-center gap-2">
              <Settings size={12} /> Super Admin · Diplomexa Academy
            </p>
            <h1 className="mt-1 text-3xl md:text-4xl font-bold leading-tight">
              Live Classroom · Provider Settings
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-blue-100">
              Adapter health. These provider names appear ONLY here — learners and parents never see them.
            </p>
          </div>
        </section>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F]">Active default</h2>
            <button type="button"
                      onClick={reconcile}
                      disabled={reconciling}
                      data-testid="lmslive-reconcile-recordings"
                      className="inline-flex items-center gap-1.5 rounded-lg bg-[#0A192F] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white hover:bg-[#0F2C50] disabled:opacity-50">
              <RefreshCw size={11} className={reconciling ? "animate-spin" : ""} /> Reconcile recordings
            </button>
          </div>
          <p className="mt-2 text-sm text-slate-700">
            <span className="font-bold text-[#0A192F]">{data?.active_default || "—"}</span>{" "}
            <span className="text-slate-500">(LIVE_CLASS_PROVIDER env)</span>
          </p>
        </div>

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F]">
            Adapters
          </h2>
          <div className="mt-3 space-y-2">
            {data ? Object.entries(data.providers).map(([name, info]) => (
              <div key={name}
                    data-testid={`lmslive-provider-${name}`}
                    className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-[#0A192F]">{name}</p>
                  <p className="text-[11px] text-slate-500">
                    {info.future ? "Adapter not yet implemented." :
                     info.active ? "Active · ready to handle live sessions." :
                                   "Inactive — configure env vars to activate."}
                  </p>
                </div>
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ring-1 ${
                  info.active
                    ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                    : info.future
                      ? "bg-slate-100 text-slate-500 ring-slate-300"
                      : "bg-amber-50 text-amber-700 ring-amber-300"
                }`}>
                  {info.active
                    ? <><CheckCircle2 size={10} /> active</>
                    : info.future
                      ? "future"
                      : <><AlertCircle size={10} /> inactive</>}
                </span>
              </div>
            )) : <p className="text-sm text-slate-500">Loading…</p>}
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F] flex items-center gap-2">
            <ShieldCheck size={14} className="text-[#D4AF37]" /> Required env to activate 100ms
          </h2>
          <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
            {(data?.env_required_for_100ms || []).map(k => (
              <div key={k}
                    className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs font-mono text-slate-700">
                {k}
              </div>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-slate-500">
            Add these to <span className="font-mono">/app/backend/.env</span> and restart the backend
            to activate. The adapter swaps in transparently — no app changes required.
          </p>
        </section>
      </div>
    </DashboardLayout>
  );
}
